'use client';

import { useState, useEffect } from 'react';
import { supabaseBibleRepository } from '@/lib/repositories/supabaseBibleRepository';
import PropTypes from 'prop-types';

export default function BibleReaderTopBar({
  selectedBook,
  selectedChapter,
  selectedVersion,
  selectedLanguage,
  viewMode,
  searchQuery,
  availableBooks = [],
  onBookChange,
  onChapterChange,
  onVersionChange,
  onLanguageChange,
  onViewModeChange,
  onSearchChange,
  bookInfo
}) {
  const [showBookMenu, setShowBookMenu] = useState(false);
  const [showChapterMenu, setShowChapterMenu] = useState(false);
  const [allBooks, setAllBooks] = useState({ oldTestament: [], newTestament: [] });
  const [translations, setTranslations] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(false);

  // Fetch books from Supabase
  useEffect(() => {
    async function fetchBooks() {
      setLoadingBooks(true);
      try {
        const translationCode = selectedVersion?.toUpperCase() === 'KJV' ? 'KJV_EN' : selectedVersion?.toUpperCase();
        const books = await supabaseBibleRepository?.listBooks(translationCode);
        
        // Separate Old and New Testament books
        const oldTestament = books?.filter(b => b?.testament === 'Old') || [];
        const newTestament = books?.filter(b => b?.testament === 'New') || [];
        
        setAllBooks({ oldTestament, newTestament });
      } catch (error) {
        console.error('Error fetching books:', error);
      } finally {
        setLoadingBooks(false);
      }
    }

    fetchBooks();
  }, [selectedVersion]);

  // Fetch translations
  useEffect(() => {
    async function fetchTranslations() {
      try {
        const translationsList = await supabaseBibleRepository?.listTranslations();
        setTranslations(translationsList);
      } catch (error) {
        console.error('Error fetching translations:', error);
      }
    }

    fetchTranslations();
  }, []);

  // Map book code to UI-friendly format
  const getBookIdFromCode = (bookCode) => {
    return bookCode?.toLowerCase();
  };

  const chapters = bookInfo ? Array.from({ length: bookInfo?.chapterCount }, (_, i) => i + 1) : [];

  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-3 flex-wrap">
        {/* Book Selector */}
        <select
          value={selectedBook || ''}
          onChange={(e) => onBookChange(e?.target?.value)}
          className="px-3 py-2 bg-background border border-border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="" disabled>Select Book</option>
          {availableBooks?.map((book) => (
            <option key={book?.bookCode} value={book?.bookCode}>
              {book?.bookName || book?.shortName}
            </option>
          ))}
        </select>

        {/* Chapter Selector */}
        <div className="relative">
          <button
            onClick={() => setShowChapterMenu(!showChapterMenu)}
            className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 transition-colors font-medium"
          >
            <span>Chapter {selectedChapter}</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showChapterMenu && (
            <div className="absolute top-full left-0 mt-2 w-64 max-h-80 overflow-y-auto bg-card border border-border rounded-lg shadow-xl z-50">
              <div className="p-2 grid grid-cols-5 gap-1">
                {chapters?.map(chapter => (
                  <button
                    key={chapter}
                    onClick={() => {
                      onChapterChange(chapter);
                      setShowChapterMenu(false);
                    }}
                    className={`px-2 py-2 text-sm rounded hover:bg-accent transition-colors ${
                      selectedChapter === chapter ? 'bg-accent text-accent-foreground font-medium' : ''
                    }`}
                  >
                    {chapter}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Version Selector */}
        <select
          value={selectedVersion}
          onChange={(e) => onVersionChange(e?.target?.value)}
          className="px-4 py-2 bg-accent text-accent-foreground rounded-lg border-0 font-medium cursor-pointer hover:bg-accent/90 transition-colors"
        >
          {translations?.map(trans => (
            <option key={trans?.id} value={trans?.code?.toLowerCase()?.replace('_en', '')}>
              {trans?.code}
            </option>
          ))}
        </select>

        {/* Language Selector - Keep simple for now */}
        <select
          value={selectedLanguage}
          onChange={(e) => onLanguageChange(e?.target?.value)}
          className="px-4 py-2 bg-accent text-accent-foreground rounded-lg border-0 font-medium cursor-pointer hover:bg-accent/90 transition-colors"
        >
          <option value="en">🇺🇸 English</option>
        </select>

        {/* View Mode Toggle */}
        <button
          onClick={() => onViewModeChange(viewMode === 'single' ? 'parallel' : 'single')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            viewMode === 'parallel' ? 'bg-primary text-primary-foreground' : 'bg-accent text-accent-foreground hover:bg-accent/90'
          }`}
          title={viewMode === 'single' ? 'Show Parallel View' : 'Show Single View'}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
        </button>

        {/* Search Input */}
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e?.target?.value)}
              placeholder="Search scripture..."
              className="w-full px-4 py-2 pl-10 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <svg className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

BibleReaderTopBar.propTypes = {
  selectedBook: PropTypes?.string,
  selectedChapter: PropTypes?.number?.isRequired,
  selectedVersion: PropTypes?.string?.isRequired,
  selectedLanguage: PropTypes?.string?.isRequired,
  viewMode: PropTypes?.oneOf(['single', 'parallel'])?.isRequired,
  searchQuery: PropTypes?.string,
  availableBooks: PropTypes?.arrayOf(PropTypes?.shape({
    bookCode: PropTypes?.string?.isRequired,
    bookName: PropTypes?.string?.isRequired,
    shortName: PropTypes?.string,
  })),
  onBookChange: PropTypes?.func?.isRequired,
  onChapterChange: PropTypes?.func?.isRequired,
  onVersionChange: PropTypes?.func?.isRequired,
  onLanguageChange: PropTypes?.func?.isRequired,
  onViewModeChange: PropTypes?.func?.isRequired,
  onSearchChange: PropTypes?.func?.isRequired,
  bookInfo: PropTypes?.object,
};